import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateTeaPurchaseRequest,
    ListTeaPurchasesParams,
    MarkPaidRequest,
    MarkProcessedRequest,
    PatchTeaPurchaseRequest,
} from '@/lib/api/tea-purchase-api'
import { teaPurchaseApi } from '@/lib/api/tea-purchase-api'
import { m } from '@/lib/paraglide/messages'

export const teaPurchaseKeys = {
    all: ['tea-purchases'] as const,
    list: (params: ListTeaPurchasesParams | undefined) =>
        [...teaPurchaseKeys.all, 'list', params ?? {}] as const,
    detail: (id: string) => [...teaPurchaseKeys.all, 'detail', id] as const,
}

export function useTeaPurchasesList(params?: ListTeaPurchasesParams) {
    return useQuery({
        queryKey: teaPurchaseKeys.list(params),
        queryFn: async () => {
            const res = await teaPurchaseApi.list(params)
            return res
        },
    })
}

export function useTeaPurchase(id: string | undefined) {
    return useQuery({
        queryKey: id
            ? teaPurchaseKeys.detail(id)
            : ['tea-purchases', 'detail', 'none'],
        enabled: !!id,
        queryFn: async () => {
            const res = await teaPurchaseApi.get(id ?? '')
            return res.data
        },
    })
}

export function useCreateTeaPurchase() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateTeaPurchaseRequest) =>
            teaPurchaseApi.create(data),
        onSuccess: () => {
            toast.success(m.tea_purchase_create_success())
            void qc.invalidateQueries({ queryKey: teaPurchaseKeys.all })
        },
        onError: () => {
            toast.error(m.tea_purchase_create_error())
        },
    })
}

export function usePatchTeaPurchase() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: PatchTeaPurchaseRequest
        }) => teaPurchaseApi.patch(id, data),
        onSuccess: () => {
            toast.success(m.tea_purchase_update_success())
            void qc.invalidateQueries({ queryKey: teaPurchaseKeys.all })
        },
        onError: (err: unknown) => {
            const msg = (err as { message?: string })?.message
            toast.error(msg || m.tea_purchase_update_error())
        },
    })
}

export function useMarkTeaPurchasePaid() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: MarkPaidRequest }) =>
            teaPurchaseApi.markPaid(id, data),
        onSuccess: () => {
            toast.success(m.tea_purchase_mark_paid_success())
            void qc.invalidateQueries({ queryKey: teaPurchaseKeys.all })
        },
        onError: () => {
            toast.error(m.tea_purchase_mark_paid_error())
        },
    })
}

export function useMarkTeaPurchaseProcessed() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: MarkProcessedRequest
        }) => teaPurchaseApi.markProcessed(id, data),
        onSuccess: () => {
            toast.success(m.tea_purchase_mark_processed_success())
            void qc.invalidateQueries({ queryKey: teaPurchaseKeys.all })
        },
        onError: () => {
            toast.error(m.tea_purchase_mark_processed_error())
        },
    })
}
